export type Location = {
  lat: number
  lon: number
}

export const AZURE_MAPS_SUBSCRIPTION_KEY = import.meta.env.VITE_AZURE_MAPS_SUBSCRIPTION_KEY

const AZURE_MAPS_CSS_URL = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css'
const AZURE_MAPS_SCRIPT_URL = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.js'

let azureMapsSdkRequest: Promise<unknown> | null = null

const getAtlasGlobal = () => (window as Window & { atlas?: unknown }).atlas

export const loadAzureMapsSdk = <TAtlas>(): Promise<TAtlas> => {
  const atlas = getAtlasGlobal()
  if (atlas) return Promise.resolve(atlas as TAtlas)
  if (azureMapsSdkRequest) return azureMapsSdkRequest as Promise<TAtlas>

  if (!document.querySelector(`link[href="${AZURE_MAPS_CSS_URL}"]`)) {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = AZURE_MAPS_CSS_URL
    document.head.append(link)
  }

  azureMapsSdkRequest = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${AZURE_MAPS_SCRIPT_URL}"]`)
    const script = existingScript ?? document.createElement('script')

    script.src = AZURE_MAPS_SCRIPT_URL
    script.async = true
    script.addEventListener('load', () => {
      const loadedAtlas = getAtlasGlobal()
      if (loadedAtlas) resolve(loadedAtlas)
      else reject(new Error('Azure Maps SDK loaded without atlas global'))
    })
    script.addEventListener('error', () => reject(new Error('Azure Maps SDK failed to load')))

    if (!existingScript) document.head.append(script)
  })

  return azureMapsSdkRequest as Promise<TAtlas>
}

const isAppleMobile = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const getDirectionsUrl = ({ lat, lon }: Location, label: string) => {
  if (isAppleMobile()) {
    const params = new URLSearchParams({ destination: `${lat},${lon}` })
    return `geo-navigation://directions?${params.toString()}`
  }

  if (/Android/.test(navigator.userAgent)) return `geo:0,0?q=${lat},${lon}(${encodeURIComponent(label)})`

  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
}
